import React, { BaseSyntheticEvent, MouseEvent, useState } from 'react';

import { useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/sharp-regular-svg-icons';
import { Box, Button, Stack, Typography, Menu, MenuItem } from '@mui/material';
import {
    CustomizerReducerType,
    useIdPrescripteurCustomizer,
    usePrescripteursCustomizer,
    setIdPrescripteur,
} from '@europrocurement/l2d-domain';
import { demoIcon } from '@europrocurement/l2d-icons';
import { useFullLayout } from '../../templates/FullLayout/FullLayoutContext';
import { SkeletonLoader, TextBone } from '../../skeletons';
import { TooltipedIcon } from '../TooltipedIcon';

export const DropdownPrescriber: React.FunctionComponent = function () {
    const dispatch = useDispatch();

    const { isPrescriberSwitchDisabled, prescriberDisplayOverride } = useFullLayout();
    const idPrescripteurCustomizer = useIdPrescripteurCustomizer();
    const effectivePrescriberId = prescriberDisplayOverride ?? idPrescripteurCustomizer;
    const PrescripteursCustomizer = usePrescripteursCustomizer();

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const open = Boolean(anchorEl);

    const handleClickSelectIdSociete = (event: MouseEvent<HTMLButtonElement>) =>
        setAnchorEl(event.currentTarget);
    const handleCloseSelectIdSociete = () => setAnchorEl(null);
    const handleChangePrescriber = (e: BaseSyntheticEvent) => {
        dispatch(setIdPrescripteur(e.target.value as CustomizerReducerType['xIdSociete']));
        handleCloseSelectIdSociete();
    };

    const currentRaisonSciale = PrescripteursCustomizer.find(
        (pr) => pr.id === effectivePrescriberId,
    )?.raisonSociale;

    const currentIsDemo = PrescripteursCustomizer.find(
        (pr) => pr.id === effectivePrescriberId,
    )?.demo;

    return (
        <SkeletonLoader
            isLoading={PrescripteursCustomizer.length <= 0}
            skeleton={
                <TextBone
                    sx={{
                        height: '32px',
                        width: '280px',
                    }}
                />
            }
        >
            <Box>
                <Button
                    id="basic-button"
                    aria-controls={open ? 'basic-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                    onClick={(e) => {
                        if (!isPrescriberSwitchDisabled && PrescripteursCustomizer.length > 1) {
                            handleClickSelectIdSociete(e);
                        }
                    }}
                    color="inherit"
                >
                    <Stack
                        alignItems="center"
                        display="flex"
                        flexDirection="row"
                        gap={1}
                    >
                        {currentRaisonSciale && currentIsDemo && (
                            <TooltipedIcon
                                icon={demoIcon}
                                tooltip="Démo"
                                size="xs"
                                color="error"
                            />
                        )}
                        <Typography
                            variant="l2dbodys"
                            color="text.gris50"
                        >
                            {currentRaisonSciale || 'Veuillez sélectionner un prescripteur'}
                        </Typography>
                        {!isPrescriberSwitchDisabled && PrescripteursCustomizer.length > 1 ? (
                            <FontAwesomeIcon
                                icon={faChevronDown}
                                flip={open ? 'vertical' : undefined}
                            />
                        ) : null}
                    </Stack>
                </Button>

                <Menu
                    id="basic-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleCloseSelectIdSociete}
                    MenuListProps={{
                        'aria-labelledby': 'basic-button',
                    }}
                >
                    {PrescripteursCustomizer.map((prescripteur) => (
                        <MenuItem
                            key={prescripteur.id}
                            value={prescripteur.id}
                            onClick={handleChangePrescriber}
                        >
                            {prescripteur.demo && (
                                <TooltipedIcon
                                    icon={demoIcon}
                                    tooltip="Démo"
                                    size="xs"
                                    color="error"
                                />
                            )}
                            {prescripteur.raisonSociale} {prescripteur.siret}
                        </MenuItem>
                    ))}
                </Menu>
            </Box>
        </SkeletonLoader>
    );
};
